import { Button, Group, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import AccountDetailsForm from "src/components/pages/Settings/AccountDetails/AccountDetailsForm";
import DSSPromoSection from "src/components/pages/Settings/AccountDetails/DSSPromoSection";
import ImageUploader from "src/components/shared/ImageUploader/ImageUploader";
import AuthFieldForm from "src/components/pages/Settings/AuthFieldForm";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import Loader from "src/components/shared/Loader/Loader";
import { useGetApi, useIsOnline, usePostApi } from "src/hooks";
import { PageContainer } from "src/styled";

import {
  AccountDetailsContainer,
  AccountDetailsGrid,
  CompactUserSettingsContainer,
  CredentialsSection,
  RightColumnSection,
  SectionDivider,
} from "./AccountDetails.styled";
import styles from "./AccountDetails.module.scss";
import { mapAccountDetails } from "./AccountDetails.utils";
import { Notification } from "../../../utils/Notification";

const AccountDetails = () => {
  const isOnline = useIsOnline();
  const { post, isLoading: isLoadingPost } = usePostApi();
  const [opened, { open, close }] = useDisclosure(false);

  const { data, setData, isLoading, refetch } = useGetApi({
    request: "get-settings",
    body: { params: { sect: "_account" } },
    mapper: mapAccountDetails,
  });

  const setSignature = (image: string) => {
    setData(
      prev => prev && { ...prev, signature: { ...prev.signature, image } },
    );
  };

  const confirmSave = async () => {
    if (!data?.signature) return;
    const dateUploaded = new Date();
    await post({
      request: "save-user-sig",
      body: {
        params: {
          imgBase64: data.signature.image,
          sigdate: dateUploaded.toISOString(),
        },
      },
      onSuccess: () => {
        setData(
          prev =>
            prev && {
              ...prev,
              signature: {
                ...prev.signature,
                dateUploaded: {
                  utc: dateUploaded.toISOString(),
                  lt: dateUploaded.toUTCString(),
                },
              },
            },
        );
        close();
        Notification(
          "Success",
          "Signature has been successfully changed.",
          "green",
        );
      },
    });
  };

  const handleSave = () => {
    open();
  };

  return (
    <PageContainer>
      <Breadcrumbs />

      <AccountDetailsContainer>
        {!data || isLoading ? (
          <Loader />
        ) : (
          <AccountDetailsGrid>
            <AccountDetailsForm
              titleStrings={data.name}
              rolename={data.rank}
              formData={data.form}
              refetch={refetch}
            />
            <CredentialsSection>
              {isOnline && (
                <CompactUserSettingsContainer className={styles.credentialsCard}>
                  <AuthFieldForm
                    type="pin"
                    minLength={4}
                    maxLength={4}
                    embedded
                  />
                  <SectionDivider />
                  <AuthFieldForm
                    type="password"
                    minLength={8}
                    maxLength={28}
                    embedded
                  />
                  <SectionDivider />
                  <AuthFieldForm type="passphrase" embedded />
                </CompactUserSettingsContainer>
              )}
            </CredentialsSection>
            <RightColumnSection>
              <CompactUserSettingsContainer className={styles.signatureCard}>
                <ImageUploader
                  embedded
                  titleClassName={styles.sectionTitle}
                  image={data.signature.image}
                  setImage={setSignature}
                  handleSave={handleSave}
                  isLoading={isLoadingPost}
                  description="Please ensure your uploaded signature has a white background."
                  lastUploadedDate={data.signature.dateUploaded}
                />
              </CompactUserSettingsContainer>
              <DSSPromoSection formData={data.form} refetch={refetch} />
            </RightColumnSection>
          </AccountDetailsGrid>
        )}
      </AccountDetailsContainer>
      <Modal opened={opened} onClose={close} title="Confirm Signature" centered>
        <Text size="sm" mb="lg">
          Please confirm this is your authorised digital signature.
        </Text>
        <Group justify="right" gap="sm">
          <Button variant="outline" onClick={close}>
            No
          </Button>
          <Button onClick={confirmSave} loading={isLoadingPost}>
            Yes
          </Button>
        </Group>
      </Modal>
    </PageContainer>
  );
};

export default AccountDetails;
